import React, { Component } from 'react';

import Configs from 'Configuration';
import FhtFetchUrl from 'Utils/FhtFetchUrl.jsx';

import {
  Box,
  CardActionArea,
  CircularProgress,
  Fade,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@material-ui/core';
import { withTheme } from '@material-ui/core/styles';

import { linearGradientDef } from '@nivo/core';
import { ResponsiveBar } from '@nivo/bar';

import SnackbarContentWrapper from 'components/SnackbarContentWrapper';

const chartTheme = {
  "text": {
    "fontSize": 20,
    "fontWeight": 700,
    "fill": "#333333",
    "outlineWidth": 0,
    "outlineColor": "transparent"
  },
  "axis": {
    "legend": {
      "text": {
        "fontSize": 12,
        "fontWeight": 700,
        "fill": "#777777"
      }
    },
    "ticks": {
      "line": {
        "stroke": "#777777",
        "strokeWidth": 1
      },
      "text": {
        "fontSize": 11,
        "fill": "#777777",
        "outlineWidth": 0,
        "outlineColor": "transparent"
      }
    }
  },
  "grid": {
    "line": {
      "stroke": "#dddddd",
      "strokeWidth": 1
    }
  },
};

const timescaleToTimeframe = {
  "day": 7,
  "week": 4,
  "month": 12
};

const timescaleToString = {
  "day": "7 days",
  "week": "4 weeks",
  "month": "12 months"
};

class WebProjectHome extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      // Snackbar
      openSnackbar: false,
      snackbarMessage: null,
      snackbarVariant: Configs.snackbarVariants.info,
      // Stats
      smsMessagesLastWeek: undefined,
      doNotContactResponses: undefined,
      patientsResponded: undefined,
      smsMessagesToBeSent: undefined,
      // Charts
      selectedStatKey: "smsMessagesSent",
      chartTimescale: "week",
      isChartExiting: false,
      smsMessagesChart: [],
      doNotContactResponsesChart: [],
      patientsRespondedChart: [],
      smsMessagesToBeSentChart: []
    };
  }

  render() {
    if (this.state.rerenderChart !== false)
      this.getSmsStats();

    return (
      <Box maxWidth={1280} margin="auto">
        {this.renderSnackbar()}

        <Typography variant="h2" gutterBottom>WEB Project</Typography>
        <Typography variant="subtitle1" align="left" gutterBottom>
          WEB aims to improve the capability of primary care to respond to victim-survivors and coordinate referrals for women and non-binary people.
        </Typography>

        <Box marginY={3}>
          <Typography variant="h5" gutterBottom>Overview for your practice</Typography>
          <Box
            style={{
              background: `linear-gradient(-20deg, ${this.props.theme.palette.primary.main}, ${this.props.theme.palette.primary.light})`,
            }}
            width={120}
            height={2}
            marginY={2}
          />
          <Typography variant="subtitle1" color="textSecondary" gutterBottom>Click on a stat below to view more detailed information</Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            {this.renderStat(this.state.smsMessagesLastWeek, `SMS ${this.state.smsMessagesLastWeek == 1 ? "message" : "messages"} sent`, "In the last 7 days", "smsMessagesSent")}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {this.renderStat(this.state.doNotContactResponses, `${this.state.doNotContactResponses == 1 ? "Patient" : "Patients"} opted out`, "Currently opted out patients", "doNotContactResponses")}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {this.renderStat(this.state.patientsResponded, `Patient ${this.state.patientsResponded == 1 ? "response" : "responses"}`, "Total since the start of the WEB project", "patientResponses")}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {this.renderStat(this.state.smsMessagesToBeSent, `SMS ${this.state.smsMessagesToBeSent == 1 ? "message" : "messages"} to be sent`, "Currently pending unsent SMS messages", "smsMessagesToBeSent")}
          </Grid>
          <Grid item xs={12}>
            {this.renderChart(this.state.smsMessagesChart, "SMS messages sent", `Over the last ${timescaleToString[this.state.chartTimescale]}`, `No SMS messages sent in the last ${timescaleToString[this.state.chartTimescale]}`, "smsMessagesSent")}
            {this.renderChart(this.state.doNotContactResponsesChart, "Patients opted out", `Over the last ${timescaleToString[this.state.chartTimescale]}`, `No patients opted out in the last ${timescaleToString[this.state.chartTimescale]}`, "doNotContactResponses")}
            {this.renderChart(this.state.patientsRespondedChart, "Patient responses", `Over the last ${timescaleToString[this.state.chartTimescale]}`, `No patient responses in the last ${timescaleToString[this.state.chartTimescale]}`, "patientResponses")}
            {this.renderChart(this.state.smsMessagesToBeSentChart, "SMS message requests", `SMS requests made by doctors and appointment reminders over the last ${timescaleToString[this.state.chartTimescale]}`, `No SMS messages requested in the last ${timescaleToString[this.state.chartTimescale]}`, "smsMessagesToBeSent")}
          </Grid>
        </Grid>
      </Box>
    );
  }

  renderSnackbar() {
    return (
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={this.state.openSnackbar}
        autoHideDuration={4000}
        onClose={() => this.setState({ openSnackbar: false })}
      >
        <SnackbarContentWrapper
          onClose={() => this.setState({ openSnackbar: false })}
          variant={this.state.snackbarVariant}
          message={this.state.snackbarMessage}
        />
      </Snackbar>
    );
  }

  renderStat(statNumber, statHeading, statDescription, statKey) {
    const isSelected = this.state.selectedStatKey === statKey;

    return (
      <Box height="100%">
        <Paper style={{ overflow: "hidden", height: "100%" }} elevation={isSelected ? 1 : 3}>
          <CardActionArea style={{ height: "100%" }} onClick={() => this.setState({ selectedStatKey: statKey, isChartExiting: true })}>
            <Box
              position="relative"
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="flex-start"
              height="100%"
              minHeight="8rem"
            >
              <Box
                style={{
                  opacity: isSelected ? 0.15 : 0,
                  backgroundColor: this.props.theme.palette.primary.light,
                  transition: `opacity ${this.props.theme.transitions.duration.shorter}ms ${this.props.theme.transitions.easing.easeInOut}`
                }}
                position="absolute"
                top={0}
                left={0}
                bottom={0}
                right={0}
              />
              <Box
                style={{
                  opacity: isSelected ? 1 : 0,
                  transition: `opacity ${this.props.theme.transitions.duration.shorter}ms ${this.props.theme.transitions.easing.easeInOut}`
                }}
                border={`3px solid ${this.props.theme.palette.primary.main}`}
                borderRadius={this.props.theme.shape.borderRadius}
                position="absolute"
                top={0}
                left={0}
                bottom={0}
                right={0}
              />
              <Box
                position="relative"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="2rem"
                width="100%"
                height="5rem"
              >
                <Box
                  style={{
                    background: `linear-gradient(-20deg, ${this.props.theme.palette.primary.main}, ${this.props.theme.palette.primary.light})`,
                  }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  position="absolute"
                  width="4rem"
                  height="4rem"
                  borderRadius="50%"
                />
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  position="absolute"
                  color="primary.contrastText"
                >
                  {statNumber}
                </Box>
              </Box>
              <Box
                width="100%"
                paddingY={1}
                paddingX={2}
                flexGrow={1}
              >
                {statHeading &&
                  <Typography
                    variant="h6"
                    style={{
                      position: "relative",
                    }}
                  >
                    {statHeading}
                  </Typography>
                }
                {statDescription &&
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {statDescription}
                  </Typography>
                }
              </Box>
            </Box>
          </CardActionArea>
        </Paper>
      </Box>
    );
  }

  addDays(date, days) {
    var result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  renderChart(data, chartTitle, chartDescription, noDataMessage, chartKey) {
    const isSelected = this.state.selectedStatKey === chartKey;

    return (
      <Fade
        onExit={() => this.setState({ isChartExiting: true })}
        onExiting={() => this.setState({ isChartExiting: true })}
        onExited={() => this.setState({ isChartExiting: false })}
        in={isSelected && !this.state.isChartExiting}
        mountOnEnter
        unmountOnExit
      >
        <Paper>
          <Box padding={3}>
            <Box display="flex" flexDirection="row" alignItems="center">
              <Box>
                {chartTitle &&
                  <Typography variant="h6" gutterBottom>
                    {chartTitle}
                  </Typography>
                }
                {chartDescription &&
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {chartDescription}
                  </Typography>
                }
              </Box>
              <Box flexGrow={1}></Box>
              <Box>
                <Select
                  variant="outlined"
                  labelId={`${chartTitle.toLowerCase()}-timeframe-label`}
                  id={`${chartTitle.toLowerCase()}-timeframe`}
                  onChange={(event) => {
                    this.setState({ chartTimescale: event.target.value, rerenderChart: true });
                  }}
                  value={this.state.chartTimescale}
                >
                  <MenuItem value="day">7 Days</MenuItem>
                  <MenuItem value="week">4 Weeks</MenuItem>
                  <MenuItem value="month">12 Months</MenuItem>
                </Select>
              </Box>
            </Box>
            {this.state.isLoading &&
              <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" paddingTop={1}>
                <CircularProgress />
              </Box>
            }
            {!this.state.isLoading && (data.some(x => x.statValue > 0) ?
              <Box height={320}>
                <ResponsiveBar
                  data={data}
                  keys={["statValue"]}
                  indexBy="statDate"
                  axisBottom={{
                    orient: "bottom",
                    legend: "Date",
                    legendOffset: 36,
                    legendPosition: "middle",
                    format: d => {
                      if (this.state.chartTimescale === "day")
                        return new Intl.DateTimeFormat("en", { day: "numeric", month: "short" }).format(d);
                      if (this.state.chartTimescale === "week")
                        return `${new Intl.DateTimeFormat("en", { day: "numeric", month: "short" }).format(d)} - ${new Intl.DateTimeFormat("en", { day: "numeric", month: "short" }).format(this.addDays(d, 6))}`;
                      if (this.state.chartTimescale === "month")
                        return new Intl.DateTimeFormat("en", { month: "short", year: "2-digit" }).format(d);
                    }
                  }}
                  margin={{ top: 30, right: 30, bottom: 48, left: 30 }}
                  theme={chartTheme}
                  defs={[
                    linearGradientDef("primaryGradient", [
                      { offset: 0, color: this.props.theme.palette.primary.light },
                      { offset: 100, color: this.props.theme.palette.primary.main },
                    ]),
                  ]}
                  fill={[
                    { match: "*", id: "primaryGradient" },
                  ]}
                  labelSkipWidth={12}
                  labelSkipHeight={12}
                  labelTextColor={this.props.theme.palette.primary.contrastText}
                  isInteractive={false}
                  animate={false}
                />
              </Box> :
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                paddingTop={2}
              >
                <Typography variant="h6" color="textSecondary">{noDataMessage}</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Fade>
    );
  }

  async getSmsStats() {
    try {
      this.setState({
        rerenderChart: false,
        isLoading: true,
        smsMessagesChart: [],
        doNotContactResponsesChart: [],
        patientsRespondedChart: [],
        smsMessagesToBeSentChart: []
      });

      const chartTimeframe = timescaleToTimeframe[this.state.chartTimescale];
      const api = FhtFetchUrl(`${Configs.getSmsStats}?statTimescale=${this.state.chartTimescale}&statTimeframe=${chartTimeframe}&statProjectId=${Configs.AppointmentToSmsProjectId.WebProject}`);
      const response = await api.get();

      response.smsMessagesChart = response.smsMessagesChart.map(statItem => {
        return {
          statDate: new Date(statItem.statDate),
          statValue: statItem.statValue
        };
      });

      response.doNotContactResponsesChart = response.doNotContactResponsesChart.map(statItem => {
        return {
          statDate: new Date(statItem.statDate),
          statValue: statItem.statValue
        };
      });

      response.patientsRespondedChart = response.patientsRespondedChart.map(statItem => {
        return {
          statDate: new Date(statItem.statDate),
          statValue: statItem.statValue
        };
      });

      response.smsMessagesToBeSentChart = response.smsMessagesToBeSentChart.map(statItem => {
        return {
          statDate: new Date(statItem.statDate),
          statValue: statItem.statValue
        };
      });

      this.setState({ ...response });
    }
    catch (e) {
      console.error(e);
      this.setState({ snackbarMessage: "Could get SMS settings", snackbarVariant: "error", openSnackbar: true });
    }
    finally {
      this.setState({ isLoading: false });
    }
  }
}

export default withTheme(WebProjectHome);
